import { IMSObject, IMSId } from './ims-object';

/**
 * An ims/ActivityConfirmation as delivered by the standalone
 * `syncactivityconfirmations` endpoint.
 *
 * The AC infos used to reach the app only piggybacked on the ExtFile DTO
 * (`ExtFile.acukey`, `ExtFile.acStatus`, ...). ExtFiles of a type with
 * synctype `never` are never synced to the app though, so for those the app
 * never learned about the ActivityConfirmation and could not take over a
 * draft. This model backs the dedicated sync that closes that gap.
 *
 * The backend sends the ACs of the synced ExtFiles (same scoping as
 * syncextfiles, hence the identical `requestingAdditionalSjobs` request param)
 * plus the ACs where the user is the `draftOwnerEmpId` or holds the job named
 * by `draftOwnerServiceJobId`.
 *
 * These records are always backend-owned: besides the sync they also arrive via
 * the `acdto` key of a dynform upload response, in the very same shape. There is
 * therefore no local-only variant of an AC.
 */
export class ActivityConfirmation extends IMSObject {
  static fromDto(dto: ActivityConfirmationDTO, databag?: any) {
    const obj = new this(
      {
        imsId: dto.id,
        objRef: 'ims/ActivityConfirmation',
      },
      dto.label,
      dto.acukey,
      dto.status,
      dto.islocked,
      dto.isconflictversion,
      dto.reftstamp,
      dto.eventtstamp,
      dto.ownerid,
      dto.ownerobjref,
      dto.reportfileid,
      dto.origfileid,
      dto.dynformid,
      dto.filledformdata,
      dto.numberrangevalue,
      dto.draftownerempid,
      dto.draftownermcid,
      dto.draftownerservicejobid,
      dto.jobsectionid,
      dto.entitycreatedtstamp,
      dto.createuser,
      dto.createdempid,
      dto.createddeviceid,
      dto.createddevicelabel,
      dto.previousversionactconfid,
      dto.nextversionactconfid,
      dto.chainrootacid,
    );

    obj.databag = databag ?? dto;
    return obj;
  }

  constructor(
    public readonly id: IMSId,
    label: string,
    /**
     * Stable identity of the AC chain across versions - the `id` changes with
     * every new AC version, the acukey does not (see APP4-948).
     */
    public acukey: string = undefined,
    /** see AcStatus in dyn-form-data.ts */
    public acStatus: number = undefined,
    public acIsLocked: boolean = undefined,
    /**
     * APP4-948: this AC is a conflict version, i.e. a stale upload forked off the
     * chain and the next open needs a version choice.
     */
    public isConflictVersion: boolean = undefined,
    public refTstamp: number = undefined,
    public eventTstamp: number = undefined,
    public ownerId: number = undefined,
    public ownerObjRef: string = undefined,
    public reportFileId: number = undefined,
    public origFileId: number = undefined,
    public dynFormId: number = undefined,
    /**
     * The filled form JSON of the draft, same shape as ExtFile.filledFormData:
     * { formId, formName, jobId, referenceTs, formCreatedTs, isformfinished,
     *   formdata: {...}, _formpi_abortdistribution } - see restoreCloudDraft()
     * in ext-files-logic.service.ts for the consuming side.
     *
     * Only delivered when the draft belongs to the requesting user, so an AC
     * without it is not proof that there is no draft content.
     */
    public filledFormData: string = undefined,
    public numberRangeValue: string = undefined,
    /**
     * The draft owner. `draftOwnerEmpId` and `draftOwnerServiceJobId` are
     * MUTUALLY EXCLUSIVE - a draft is owned either by an employee or by a
     * service job, never by both. Whichever is not in play is null, and both
     * being null means the AC currently has no draft owner at all.
     *
     * Consequence for writers: when one of them arrives, the other has to be
     * actively nulled, not preserved - see updateACInfo() in
     * dyn-form-data-db-dao.service.ts, which deliberately does NOT COALESCE them.
     */
    public draftOwnerEmpId: number = undefined,
    /** The device holding the baton, only meaningful with draftOwnerEmpId (APP4-948) */
    public draftOwnerMcId: number = undefined,
    /** @see draftOwnerEmpId - mutually exclusive with it */
    public draftOwnerServiceJobId: number = undefined,
    public jobSectionId: number = undefined,
    /**
     * When THIS version was created. With cloud autosave every save produces a new
     * version, so this is "last saved" for the current one. Not to be confused with
     * refTstamp, which is the form's reference date.
     */
    public entityCreatedTstamp: number = undefined,
    public createUser: string = undefined,
    public createdEmpId: number = undefined,
    public createdDeviceId: number = undefined,
    /** Display name of the creating device, e.g. "LBS JB iPhone" */
    public createdDeviceLabel: string = undefined,
    public previousVersionActConfId: number = undefined,
    /**
     * Set on every version that has been superseded. Together with
     * isConflictVersion this identifies the current version of a chain -
     * see isCurrentVersion().
     */
    public nextVersionActConfId: number = undefined,
    /**
     * KRK-45: id of the FIRST version of this chain - the stable identity of the
     * document. Do not read it directly, use chainRoot(): it is undefined for ACs
     * the backend created outside the mobile upload paths.
     *
     * Replaces the acukey as the chain identity. The acukey looked stable but is
     * rewritten by a smart renumbering (LBW-251) whenever a colleague files an
     * earlier report, which silently turned one document into two on this device.
     */
    public chainRootAcId: number = undefined,
  ) {
    super(id.imsId, id.secondaryId, label, 'ims/ActivityConfirmation');
  }

  /**
   * KRK-45: the identity of this document's version chain, usable for matching.
   *
   * Falls back to the own acid, which is correct for a single-version AC - the only
   * case where the backend leaves chainRootAcId empty (created outside the mobile
   * upload paths, or a row no update script has backfilled).
   */
  public chainRoot(): number {
    return this.chainRootAcId ?? this.getImsId();
  }

  /**
   * True for the one version of a chain that is currently in effect.
   *
   * Since APILevel 15 the sync delivers the whole version chain of a document
   * (up to 50 versions), not only its head. Every superseded version carries a
   * `nextVersionActConfId`; a conflict version is a fork that was superseded as
   * well but does not point anywhere, so it needs the second condition.
   *
   * Matters wherever an AC stands for "the state of this document" - offering a
   * draft, cleaning up finished chains - because otherwise every historical
   * version of one document would be treated as a document of its own.
   */
  public isCurrentVersion(): boolean {
    return this.nextVersionActConfId == null && !this.isConflictVersion;
  }
}

export interface ActivityConfirmationDTO {
  id?: number;
  chainrootacid?: number;
  label?: string;
  acukey?: string;
  /**
   * see AcStatus in dyn-form-data.ts
   *
   * NOTE: an AC DTO never uses `acstatus`/`acislocked` - those exist only as flat
   * keys on the outermost level of a dynform response, for the legacy code that
   * writes the AC infos straight onto the DynFormData/ExtFile.
   */
  status?: number;
  islocked?: boolean;
  isconflictversion?: boolean;
  reftstamp?: number;
  eventtstamp?: number;
  ownerid?: number;
  ownerobjref?: string;
  reportfileid?: number;
  origfileid?: number;
  dynformid?: number;
  filledformdata?: string;
  numberrangevalue?: string;
  draftownerempid?: number;
  draftownermcid?: number;
  draftownerservicejobid?: number;
  jobsectionid?: number;
  entitycreatedtstamp?: number;
  createuser?: string;
  createdempid?: number;
  createddeviceid?: number;
  createddevicelabel?: string;
  previousversionactconfid?: number;
  nextversionactconfid?: number;
}
